import { BaseSyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import ReactModal from 'react-modal';
import {ReactComponent as DeleteIcon} from '../../../images/nde-icon-delete.svg';
import { useDispatch, useSelector } from 'react-redux';
import { accountLoginStart, selectAccountLoginErrMsg, selectAccountLoginLoading, selectIsLoggedIn } from '../../../redux/account';
import { selectControls } from '../../../redux/controls';
import Spinner from '../../Spinner/Spinner';
import { TextFormControl } from '../../TextFormControl/TextFormControl';
import './LoginModal.scss';
import { ModalProps, processForgotPassword } from '../models';
import ga4 from '../../../shared/analyticsEvents';

export const LoginModal = ({ open, onClose }: ModalProps): JSX.Element => {
  const dispatch = useDispatch();

  const controls = useSelector(selectControls);

  const errMsg = useSelector(selectAccountLoginErrMsg);
  const isLoading = useSelector(selectAccountLoginLoading);
  const successful = useSelector(selectIsLoggedIn);

  ReactModal.setAppElement('#root');

  const signInForm = useRef<HTMLFormElement>(null);
  const forgotPasswordForm = useRef<HTMLFormElement>(null);

  const [forgotPasswordErrMsg, setForgotPasswordErrMsg] = useState<string>('');
  const [forgotPasswordIsLoading, setForgotPasswordIsLoading] = useState<boolean>(false);
  const [forgotPasswordSuccessful, setForgotPasswordSuccessful] = useState<boolean>(false);

  const [contentLabel, setContentLabel] = useState<string>('');
  const [isValid, setIsValid] = useState<boolean>(false);
  const [processing, setProcessing] = useState<boolean>(false);
  const [submitted, setSubmitted] = useState<boolean>(false);

  const [processingForgotPassword, setProcessingForgotPassword] = useState<boolean>(false);

  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);

  const submitSignInHandler = useCallback((event: BaseSyntheticEvent, formEl: React.RefObject<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (formEl.current) {
      const formData = new FormData(formEl.current);
      const formProps = Object.fromEntries(formData);
      dispatch(
        accountLoginStart( {
          emailId: formProps['email'] as string,
          password: formProps['password'] as string
        })
      );
      setSubmitted(true);
      setProcessing(true);
    }
  }, [dispatch]);

  const submitForgotPasswordHandler = useCallback((event: BaseSyntheticEvent, formEl: React.RefObject<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (formEl.current) {
      const formData = new FormData(formEl.current);
      const formProps = Object.fromEntries(formData);
      processForgotPassword(
        {
          emailId: formProps['email'] as string
        },
        setForgotPasswordIsLoading,
        setForgotPasswordSuccessful,
        setForgotPasswordErrMsg
      );
      setSubmitted(true);
      setProcessingForgotPassword(true);
      ga4.onGA4ForgotPassword();
    }
  }, []);

  const closeModalHandler = useCallback(() => {
    setShowForgotPassword(false);
    setSubmitted(false);
    setProcessing(false);
    setProcessingForgotPassword(false);
    setIsValid(false);
    onClose(true);
  }, [onClose]);


  // fires on mount and whenever showForgotPassword is toggled
  // sets Header Title
  useEffect(() => {
    if (controls) {
      showForgotPassword ?
        setContentLabel(controls?.ForgotHeader?.controlText) :
        setContentLabel(controls?.LoginHeader?.controlText);
    }
  }, [controls, showForgotPassword]);

  useEffect(() => {
    if (!isLoading && processing) {
      setProcessing(false);
    }
  }, [isLoading, processing]);

  useEffect(() => {
    if (!forgotPasswordIsLoading && processingForgotPassword) {
      setProcessingForgotPassword(false);
    }
  }, [forgotPasswordIsLoading, processingForgotPassword]);

  useEffect(() => {
    if (successful) {
      ga4.onGA4UserLogin();
    }
  },[successful]);


  return (
    <>
      {
        open &&
        <ReactModal
          isOpen={open}
          className="nqt-modal"
          // onAfterOpen={() => onAfterOpenCallback(winType, cancelEl, okEl)}
          overlayClassName="nqt-modal-overlay"
          shouldFocusAfterRender={true}
          contentLabel={contentLabel}
        >
          {
            !submitted || processing || processingForgotPassword ?
              showForgotPassword ?
                <>
                  <form ref={forgotPasswordForm} onSubmit={(e) => submitForgotPasswordHandler(e, forgotPasswordForm)} className='nqt-modal--container'>
                    { forgotPasswordIsLoading && <Spinner/> }
                    <div className="nqt-modal--header-group">
                      <header className='nqt-modal--title'>{contentLabel}</header>
                      <button className='nqt-modal--close-button'
                        type='button'
                        ref={(el) => !isValid && el?.focus()}
                        onClick={() => closeModalHandler()}>
                        <DeleteIcon/>
                        <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
                      </button>
                    </div>
                    <div className="nqt-modal--border">
                      <section className="nqt-acc-modal--content container">
                        <span className="row row-cols-1">
                          <span className="col nqt-acc-modal--description">
                            {
                              controls?.ForgotInstruct?.controlText
                            }
                          </span>
                        </span>
                        <hr className="row"/>
                        <div className="nqt-modal--form-controls container-fluid">
                          <div className="row row-cols-1 gy-3">
                            <div className="col container-fluid">
                              <TextFormControl id='acc-modal-email'
                                autoComplete="email"
                                containerClass="nqt-modal--text-input"
                                controlName='email'
                                controlType="email"
                                errorText={controls?.LoginValidEmail?.controlText}
                                headerText={controls?.LoginID?.controlText}
                                isValidCallback={() => setIsValid(!!forgotPasswordForm.current?.checkValidity())}
                                pattern="^[A-Za-z0-9._%+\-]+@([A-Za-z0-9-]+\.)+[A-Za-z]{1,10}$"
                                required={true}
                                setFocus={false}/>
                            </div>
                          </div>
                        </div>
                      </section>
                      <hr />
                      <div className="nqt-modal--buttons col-9 offset-3">
                        <button className="nqt-button--cancel col-4 offset-3"
                          type='button'
                          onClick={() => closeModalHandler()}>{controls?.ModalCancel?.controlText}</button>
                        <button className="nqt-button--submit col-4"
                          disabled={!isValid}
                          type="submit"
                          onClick={(e) => submitForgotPasswordHandler(e, forgotPasswordForm)}>{controls?.ForgotSend?.controlText}</button>
                      </div>
                    </div>
                  </form>
                </> :
                <>
                  <form ref={signInForm} onSubmit={(e) => submitSignInHandler(e, signInForm)} className='nqt-modal--container'>
                    { isLoading && <Spinner/> }
                    <div className="nqt-modal--header-group">
                      <header className='nqt-modal--title'>{contentLabel}</header>
                      <button className='nqt-modal--close-button'
                        type='button'
                        ref={(el) => !isValid && el?.focus()}
                        onClick={() => closeModalHandler()}>
                        <DeleteIcon/>
                        <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
                      </button>
                    </div>
                    <div className="nqt-modal--border">
                      <section className="nqt-acc-modal--content container">
                        <div className="nqt-modal--form-controls container-fluid">
                          <div className="row row-cols-1 gy-3">
                            <div className="col container-fluid">
                              <TextFormControl id='acc-modal-email'
                                autoComplete="email"
                                containerClass="nqt-modal--text-input"
                                controlName='email'
                                controlType="email"
                                errorText={controls?.LoginValidEmail?.controlText}
                                headerText={controls?.LoginID?.controlText}
                                isValidCallback={() => setIsValid(!!signInForm.current?.checkValidity())}
                                pattern="^[A-Za-z0-9._%+\-]+@([A-Za-z0-9\-]+\.)+[A-Za-z]{1,10}$"
                                required={true}/>
                            </div>
                            <div className="col container-fluid">
                              <TextFormControl id='acc-modal-pw'
                                autoComplete="current-password"
                                containerClass="nqt-modal--text-input"
                                controlName='password'
                                controlType="password"
                                errorText={controls?.LoginValidPassword?.controlText}
                                headerText={controls?.LoginPwd?.controlText}
                                //isValidCallback={() => setIsValid(!!signInForm.current?.checkValidity())}
                                onChangeCallback={(e) => {
                                  const value = e.target.value;
                                  const regex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%()_+=-]).{8,50}$/;
                                  setIsValid(regex.test(value));
                                }}
                                required={true} />
                            </div>
                            <div className="col container-fluid p-0 m-0 nqt-modal--forgot-password-container">
                              <button className='nqt-button-link nqt-login-modal--forgot-password'
                                type='button'
                                onClick={() => setShowForgotPassword(true)}>
                                {controls?.LoginForgot?.controlText}
                              </button>
                            </div>
                          </div>
                        </div>
                      </section>
                      <hr />
                      <div className="nqt-modal--buttons col-9 offset-3">
                        <button className="nqt-button--cancel col-4 offset-3"
                          type='button'
                          onClick={() => closeModalHandler()}>{controls?.ModalCancel?.controlText}</button>
                        <button className="nqt-button--submit col-4"
                          disabled={!isValid}
                          type="submit"
                          onClick={(e) => submitSignInHandler(e, signInForm)}>{controls?.LoginOK?.controlText}</button>
                      </div>
                    </div>
                  </form>
                </>
              :
              <>
                <div className='nqt-modal--container'>
                  <div className="nqt-modal--header-group">
                    <header className='nqt-modal--title'>{contentLabel}</header>
                    <button className='nqt-modal--close-button'
                      ref={(el) => el?.focus()}
                      onClick={() => closeModalHandler()}>
                      <DeleteIcon/>
                      <span className="visually-hidden">{controls?.ModalClose?.controlText}</span>
                    </button>
                  </div>
                  <div className="nqt-modal--border">
                    <section className="nqt-modal--content">
                      {
                        !showForgotPassword ?
                          successful ?
                            <div className="nqt-modal--description">
                              {
                                controls?.LoginSuccess?.controlText
                              }
                            </div> :
                            <div className="nqt-modal--description">
                              {
                                errMsg ?
                                  controls?.LoginFailure?.controlText :
                                  controls?.StandardErrorMsg?.controlText
                              }
                            </div>
                          :
                          forgotPasswordSuccessful ?
                            <div className="nqt-modal--description">
                              {
                                controls?.ForgotSent?.controlText
                              }
                            </div> :
                            <div className="nqt-modal--description">
                              {
                                forgotPasswordErrMsg ?
                                forgotPasswordErrMsg as string :
                                  controls?.StandardErrorMsg?.controlText
                              }
                            </div>
                      }
                    </section>
                  </div>
                </div>
              </>
          }
        </ReactModal>
      }
    </>
  );
};

export default LoginModal;